import { useEffect, useRef, useState } from 'react';
import { useCopyToClipboard } from '../lib/copy';

export const SHORTCUTS = {
  research: { name: 'Research in Almanac', path: '/Research-in-Almanac.shortcut' },
  inbox: { name: 'Save to Almanac Inbox', path: '/Save-to-Almanac-Inbox.shortcut' },
} as const;

/** Share a signed file without navigating the standalone app to a file preview. */
export function ShortcutInstaller({
  shortcut,
  onClose,
}: {
  shortcut: (typeof SHORTCUTS)[keyof typeof SHORTCUTS];
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  // Capture before React applies autofocus to the dialog's Back button.
  const previousFocus = useRef(
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const { copy, copiedKey, copyError } = useCopyToClipboard();
  const url = new URL(shortcut.path, window.location.origin).href;
  const standalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia?.('(display-mode: standalone)').matches === true;
  const canShare = !!file && !!navigator.share && !!navigator.canShare?.({ files: [file] });

  useEffect(() => {
    const returnFocusTo = previousFocus.current;
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
    }
    return () => {
      if (returnFocusTo?.isConnected) returnFocusTo.focus();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setFile(null);
    void (async () => {
      try {
        const response = await fetch(shortcut.path, { signal: controller.signal });
        if (!response.ok) throw new Error('Could not download the Shortcut. Try again.');
        const bytes = await response.arrayBuffer();
        // Missing static files can return the SPA shell with status 200.
        if (new TextDecoder().decode(bytes.slice(0, 4)) !== 'AEA1') {
          throw new Error('The signed Shortcut file is unavailable. Try again later.');
        }
        if (!controller.signal.aborted) {
          setFile(new File([bytes], shortcut.path.slice(1), { type: 'application/octet-stream' }));
        }
      } catch (caught) {
        if (!controller.signal.aborted) {
          setError(caught instanceof Error ? caught.message : 'Could not download the Shortcut.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [shortcut.path, attempt]);

  async function share() {
    if (!file || sharing) return;
    setError(null);
    setSharing(true);
    try {
      // No fetch/await before share: iOS requires a fresh user gesture. File
      // preparation happens separately so a slow network cannot expire it.
      await navigator.share({ files: [file] });
    } catch (caught) {
      if (!(caught instanceof Error && caught.name === 'AbortError')) {
        setError('Could not open the share sheet. Try again, or use the Safari steps below.');
      }
    } finally {
      setSharing(false);
    }
  }

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="shortcut-install-title"
      onCancel={onClose}
      onClose={onClose}
      className="m-auto max-h-[85dvh] w-[min(32rem,calc(100%-2rem))] overflow-y-auto rounded-xl bg-popover p-5 text-popover-foreground shadow-xl backdrop:bg-foreground/25"
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="shortcut-install-title" className="text-lg font-semibold">
            Install {shortcut.name}
          </h2>
          <button
            type="button"
            autoFocus
            onClick={onClose}
            className="min-h-11 rounded-lg bg-muted px-3 py-2 font-medium"
          >
            Back to Almanac
          </button>
        </div>
        <p>
          Choose Shortcuts in the share sheet. If it isn’t offered, choose Save to Files, then open
          the saved .shortcut file in Files to install it.
        </p>
        <p className="text-sm text-muted-foreground">
          Paste your capture token after “Bearer” when asked. You can dismiss the share sheet and
          return here at any time.
        </p>
        {loading ? <output>Preparing Shortcut…</output> : null}
        {canShare ? (
          <button
            type="button"
            onClick={() => void share()}
            disabled={sharing}
            className="min-h-11 rounded-lg bg-primary px-4 py-2 font-medium text-primary-foreground disabled:opacity-50"
          >
            {sharing ? 'Share sheet open…' : 'Open install sheet'}
          </button>
        ) : null}
        {!loading && file && !canShare ? (
          <p>This browser can’t share Shortcut files. Use the download options below.</p>
        ) : null}
        {error ? (
          <div className="space-y-2">
            <p role="alert">{error}</p>
            {!file ? (
              <button
                type="button"
                onClick={() => setAttempt((value) => value + 1)}
                className="min-h-11 text-primary underline"
              >
                Retry download
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="space-y-3 border-t border-border pt-4">
          {file && !standalone ? (
            <p>
              <a
                href={shortcut.path}
                download
                className="inline-flex min-h-11 items-center text-primary underline"
              >
                Download file
              </a>{' '}
              and open it in Shortcuts (or from Files on iPhone).
            </p>
          ) : null}
          <p>Or copy this link, open Safari, and paste it into the address bar to download.</p>
          <input
            aria-label="Shortcut download link"
            readOnly
            value={url}
            onFocus={(event) => event.target.select()}
            className="w-full rounded-lg bg-background px-3 py-2 text-base ring-1 ring-foreground/10"
          />
          <button
            type="button"
            onClick={() => copy('shortcut', url)}
            className="min-h-11 text-primary underline"
          >
            {copiedKey === 'shortcut' ? 'Link copied' : 'Copy download link'}
          </button>
          {copyError ? (
            <p role="alert">Could not copy. Select the link above and copy it manually.</p>
          ) : null}
        </div>
      </div>
    </dialog>
  );
}
